import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button, type ButtonProps } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface ClearNotificationsDialogProps {
  onClear: () => Promise<void>;
  disabled?: boolean;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  showLabel?: boolean;
}

export const ClearNotificationsDialog: React.FC<ClearNotificationsDialogProps> = ({
  onClear,
  disabled,
  variant = 'outline',
  size = 'sm',
  showLabel = true,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const handleConfirm = async () => {
    setIsClearing(true);
    try {
      await onClear();
      setOpen(false);
    } catch (error) {
      // Error handling is done by the parent hook; just keep the dialog open briefly.
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={disabled}
          className={!showLabel ? 'h-8 w-8 p-0' : undefined}
          aria-label={t('notifications.clearAll')}
        >
          <Trash2 className="h-4 w-4" />
          {showLabel && <span className="ml-2">{t('notifications.clearAll')}</span>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('notifications.clearAllConfirm')}</AlertDialogTitle>
          <AlertDialogDescription>{t('notifications.clearAllDescription')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isClearing}>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isClearing}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isClearing ? t('notifications.clearing') : t('notifications.clearAll')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ClearNotificationsDialog;
